import React, { FC, useEffect, useState } from 'react';
import svg4everybody from 'svg4everybody';
import { BrowserRouter, withRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Slide, ToastContainer } from 'react-toastify';
import { addYmGoalListeners } from 'src/helpers/ymevent';
import Routes from './Routes';
import { setContentPages, setEventKey, setIsRegistrationRequired, setLoggedIn } from '../stores/reducers/userData';
import { adminDomains, checkAuthorization, getHomePageContent, getFontsList, userHost } from '../api';
import {
  changeSectionsAll,
  changeSocialAll,
  changeStyleAll,
  updateFontsList,
  updateInternalizationState
} from '../stores/reducers/sections';
import 'react-toastify/dist/ReactToastify.css';
import AppendNodeElem from '../helpers/append-nodes';
import { eventKey } from '../helpers/get-event-key';
import { Preloader } from './Elements/Preloader/Preloader';
import { toggleLanguage, updateUniqueWatchKey } from '../stores/reducers/sections';
import {storageFactory} from '../helpers/storage';
import CookieAccept from './Modals/CookieAccept/CookieAccept';

const local = storageFactory(() => localStorage);

class Scroll extends React.Component<RouteComponentProps<any>> {
  componentDidUpdate(prevProps: RouteComponentProps<any>) {
    const { location } = this.props;
    if (location !== prevProps.location) {
      window.scrollTo(0, 0);
    }
  }

  render() {
    const { children } = this.props;
    return children;
  }
}
const ScrollToTop = withRouter(Scroll);

const App: FC = (): React.ReactElement => {
  const dispatch = useDispatch();
  const [isEventKeyCorrect, setIsEventKeyCorrect] = useState(false);
  const [isAppInitialized, setIsAppInitialized] = useState(false);

  useEffect(() => {
    svg4everybody();
  }, []);

  const basename = `/watch/${eventKey}/`;

  const storageUniqueKeySave = (unique_key: string): void => {
    if (eventKey) {
      const uniqueKeyData = local.getItem(eventKey);
      if (!uniqueKeyData) {
        local.setItem(eventKey, unique_key);
      }
    }
  };
  
  const storageUniqueKeyCheck = (): string => {
    if (eventKey) {
      return local.getItem(eventKey) ?? '';
    }

    return '';
  };

  const checkUrlParams = () => {
    const urlParams = window.location.search.replace(/\?/, '').split('&');
    const urlLocaleIndex = urlParams.findIndex((param: string) => {
      return param.indexOf('lang') !== -1;
    });

    const urlWatchKeyIndex = urlParams.findIndex((param: string) => {
      return param.indexOf('unique_key') !== -1;
    });

    if (urlLocaleIndex >= 0) {
      const urlLocale = urlParams[urlLocaleIndex].split('=')[1];
      dispatch(toggleLanguage(urlLocale === 'ru' ? urlLocale : 'en'));
    }

    if (urlWatchKeyIndex >= 0) {
      const watchKey = urlParams[urlWatchKeyIndex].split('=')[1];
      storageUniqueKeySave(watchKey);    
      dispatch(updateUniqueWatchKey(watchKey || ''));
    } else {
      dispatch(updateUniqueWatchKey(storageUniqueKeyCheck()));
    }
  };

  checkUrlParams();

  useEffect(() => {
    addYmGoalListeners();
    const initializeApp = async () => {
      const fontsList = await getFontsList();
      if (fontsList) {
        dispatch(updateFontsList(fontsList));
        fontsList.map((font: any): void => {
          return AppendNodeElem('style', `${userHost}/construct_api/static/${font.path}/stylesheet.css`, 'head');
        });
      }

      if (eventKey) {
        dispatch(setEventKey(eventKey));
        try {
          const res = await getHomePageContent(eventKey);
          
          if (res) {
            res.program = res.program.map((item: any) => {
              return {
                ...item,
                description: 'description' in item ? item.description : '',
              };
            });
            dispatch(setContentPages(res));
            if (res.frontData && res.frontData.styles && Object.keys(res.frontData.styles).length) {
              dispatch(changeStyleAll(res.frontData.styles));
            }
            if (res.frontData && res.frontData.socials && res.frontData.socials.length) {
              dispatch(changeSocialAll(res.frontData.socials));
            }
            if (res.frontData && res.frontData.sections && res.frontData.sections.length) {
              dispatch(changeSectionsAll(res.frontData.sections));
            }
            if (Object.prototype.hasOwnProperty.call(res, 'languages')) {
              dispatch(updateInternalizationState(res.languages));
            }
            if (res.scripts) {
              Object.keys(res.scripts).forEach((key: string): void => {
                res.scripts[key].forEach((script: string): void => {
                  if (script) {
                    AppendNodeElem('script', script, key);
                  }
                });
              });
            }
          }
          let isRegistrationRequired = true;
          if ('unauthorizedAccess' in res) {
            isRegistrationRequired = !res.unauthorizedAccess;
            dispatch(setIsRegistrationRequired(isRegistrationRequired));
          }

          if (!adminDomains.includes(window.location.hostname)) {
            const response = await checkAuthorization(eventKey);
            dispatch(setLoggedIn(response.status === 200));
          }
          setIsEventKeyCorrect(true);
          setIsAppInitialized(true);
        } catch (error) {
          setIsEventKeyCorrect(false);
          setIsAppInitialized(true);
        }
      } else {
        setIsAppInitialized(false);
      }
    };

    initializeApp();
  }, [dispatch]);

  return (
    <>
      {isAppInitialized ? (
        <>
          <BrowserRouter basename={basename}>
            <ScrollToTop>
              <Routes isEventKeyCorrect={isEventKeyCorrect} />
            </ScrollToTop>
          </BrowserRouter>
          <ToastContainer
            position="bottom-center"
            autoClose={3000}
            hideProgressBar
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable={false}
            pauseOnHover={false}
            transition={Slide}
          />
          <CookieAccept />
        </>
      ) : (
        <Preloader />
      )}
    </>
  );
};

export default App;
